/**
 * Feedback Widget Configuration
 * Centralized settings for the in-app feedback widget
 */

interface FeedbackWidgetConfig {
  /**
   * Enable/disable the feedback widget globally
   * @default true
   */
  enabled: boolean;

  /**
   * Position of the feedback button
   * @default 'bottom-right'
   */
  position: 'bottom-right' | 'bottom-left';

  /**
   * Theme variant
   * @default 'dark'
   */
  theme: 'light' | 'dark';

  /**
   * Paths where the widget should be hidden
   * Examples: '/login', '/signup', '/auth', '/public/**'
   * @default ['/login', '/signup', '/forgot-password', '/auth']
   */
  hideOnPaths: string[];

  /**
   * Show widget only for specific roles
   * Leave empty to show for all authenticated users
   * @default [] (show for all)
   */
  allowedRoles: string[];

  /**
   * Show widget only for specific zones
   * @default [] (show in all zones)
   * Examples: ['app', 'partner', 'enterprise', 'admin']
   */
  allowedZones: string[];

  /**
   * Enable debug logging
   * @default false
   */
  debug: boolean;

  /**
   * Custom success message
   * @default 'Thanks for your feedback!'
   */
  successMessage: string;

  /**
   * Custom button tooltip
   * @default 'Send us your feedback'
   */
  buttonTooltip: string;

  /**
   * Analytics integration
   * Send feedback to external analytics service
   * @default false
   */
  trackAnalytics: boolean;

  /**
   * Automatic dialog dismissal after submission (ms)
   * @default 3000
   */
  autoCloseDuration: number;

  /**
   * Show rating stars
   * @default true
   */
  showRating: boolean;

  /**
   * Show sentiment emojis
   * @default true
   */
  showSentiment: boolean;

  /**
   * Maximum feedback title length
   * @default 100
   */
  maxTitleLength: number;

  /**
   * Maximum feedback description length
   * @default 500
   */
  maxDescriptionLength: number;
}

/**
 * Default configuration for the feedback widget
 * Override with custom config in FeedbackWidgetProvider
 */
export const DEFAULT_FEEDBACK_CONFIG: FeedbackWidgetConfig = {
  enabled: true,
  position: 'bottom-right',
  theme: 'dark',
  hideOnPaths: [
    '/login',
    '/signup',
    '/forgot-password',
    '/auth',
    '/verify',
    '/reset-password',
  ],
  allowedRoles: [], // Empty means show for all
  allowedZones: [], // Empty means show in all zones
  debug: false,
  successMessage: 'Thanks for your feedback!',
  buttonTooltip: 'Send us your feedback',
  trackAnalytics: true,
  autoCloseDuration: 3000,
  showRating: true,
  showSentiment: true,
  maxTitleLength: 100,
  maxDescriptionLength: 500,
};

/**
 * Configuration preset for different environments
 */
export const FEEDBACK_PRESETS = {
  /**
   * Production: Professional, minimal
   */
  production: {
    enabled: true,
    position: 'bottom-right' as const,
    theme: 'dark' as const,
    debug: false,
    trackAnalytics: true,
  },

  /**
   * Staging: More verbose feedback
   */
  staging: {
    enabled: true,
    position: 'bottom-right' as const,
    theme: 'dark' as const,
    debug: true,
    trackAnalytics: true,
  },

  /**
   * Development: Full debug info
   */
  development: {
    enabled: true,
    position: 'bottom-left' as const,
    theme: 'dark' as const,
    debug: true,
    trackAnalytics: false,
  },

  /**
   * Demo: Always enabled, prominent
   */
  demo: {
    enabled: true,
    position: 'bottom-right' as const,
    theme: 'dark' as const,
    debug: false,
    trackAnalytics: true,
  },

  /**
   * Disabled: For testing without widget
   */
  disabled: {
    enabled: false,
    debug: false,
  },
};

/**
 * Get the appropriate config for the current environment
 */
export function getEnvironmentConfig(): Partial<FeedbackWidgetConfig> {
  const env = process.env.NODE_ENV;

  // Use production preset by default
  if (env === 'production') {
    return FEEDBACK_PRESETS.production;
  }

  if (env === 'development') {
    return FEEDBACK_PRESETS.development;
  }

  // Default to production for unknown environments
  return FEEDBACK_PRESETS.production;
}

/**
 * Merge user config with defaults
 */
export function mergeFeedbackConfig(
  userConfig?: Partial<FeedbackWidgetConfig>
): FeedbackWidgetConfig {
  return {
    ...DEFAULT_FEEDBACK_CONFIG,
    ...getEnvironmentConfig(),
    ...userConfig,
  };
}

export type { FeedbackWidgetConfig };
